import { TranscricaoDetalhe } from '../../../../interfaces/entities/Transcricao/TranscricaoDetalhe';
import { TranscricaoItemDetalhe } from '../../../../interfaces/entities/Transcricao/TranscricaoRevisao';
import { dateRegex } from '../../../../utils/DateRegex';
import { RevisaoColuna, RevisaoLinha } from './transcricaoRevisaoTypes';
import { formatTipoItemLabel } from './tipoItemUtils';

export const COLUNAS_BENEFICIARIO: RevisaoColuna[] = [
  { key: 'nome', label: 'Nome do beneficiário' },
  { key: 'cartao', label: 'Cartão Unimed' },
  { key: 'idade', label: 'Idade' },
];

export const COLUNAS_PROFISSIONAL: RevisaoColuna[] = [
  { key: 'nome', label: 'Profissional' },
  { key: 'registro', label: 'Nº Registro' },
  { key: 'uf', label: 'UF' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'cooperado', label: 'Cooperado' },
];

export const COLUNAS_PROFISSIONAL_EDITAVEL: RevisaoColuna[] = [
  ...COLUNAS_PROFISSIONAL,
  { key: 'editar', label: 'Editar', isEdit: true, align: 'center' },
];

export const COLUNAS_CID: RevisaoColuna[] = [
  { key: 'cid', label: 'CID' },
  { key: 'descricao', label: 'Descrição' },
  { key: 'emissao', label: 'Emissão do Pedido' },
];

export const COLUNAS_PROCEDIMENTOS: RevisaoColuna[] = [
  { key: 'tipoItem', label: 'Tipo do Item' },
  { key: 'codigoTuss', label: 'Código TUSS' },
  { key: 'descricao', label: 'Descrição' },
  { key: 'quantidade', label: 'Quantidade' },
];

export const COLUNAS_PROCEDIMENTOS_EDITAVEL: RevisaoColuna[] = [
  ...COLUNAS_PROCEDIMENTOS,
  { key: 'editar', label: 'Editar', isEdit: true, align: 'center' },
];

export interface DadosPedidoCardField {
  label: string;
  value: React.ReactNode;
}

export interface DadosPedidoCardRow {
  fields: DadosPedidoCardField[];
}

export const displayValue = (value?: string | number | null): string => {
  if (value === null || value === undefined) return '—';
  const text = String(value).trim();
  return text || '—';
};

const formatQuantidade = (quantidade: number): string =>
  String(quantidade).padStart(2, '0');

const formatDataCadastro = (dataCadastro?: string): string => {
  if (!dataCadastro?.trim()) return '—';
  const formatted = dateRegex(dataCadastro);
  return formatted === 'Invalid date' ? '—' : formatted;
};

const formatCooperado = (indCooperado?: string): string => {
  if (!indCooperado?.trim()) return '—';

  const normalized = indCooperado.trim().toUpperCase();
  if (normalized === 'S' || normalized === 'SIM') return 'Sim';
  if (normalized === 'N' || normalized === 'NAO' || normalized === 'NÃO') return 'Não';

  return indCooperado;
};

const splitCodDescrDiagCid = (
  combined?: string,
): { codigo?: string; descricao?: string } => {
  if (!combined?.trim()) return {};

  const text = combined.trim();
  const separatorIndex = text.indexOf(' - ');
  if (separatorIndex > 0) {
    return {
      codigo: text.slice(0, separatorIndex).trim(),
      descricao: text.slice(separatorIndex + 3).trim(),
    };
  }

  return { codigo: text };
};

const resolveCodigoCid = (detalhe: TranscricaoDetalhe): string | undefined => {
  const codigo = detalhe.codigoDiagCid?.trim();
  if (codigo) return codigo;

  const fromCombined = splitCodDescrDiagCid(detalhe.codDescrDiagCid).codigo;
  return fromCombined?.trim() || undefined;
};

const resolveDescrCid = (detalhe: TranscricaoDetalhe): string | undefined => {
  const descricao = detalhe.descrDiagCid?.trim();
  if (descricao) return descricao;

  const fromCombined = splitCodDescrDiagCid(detalhe.codDescrDiagCid).descricao;
  return fromCombined?.trim() || undefined;
};

export const mapBeneficiarioLinhas = (detalhe: TranscricaoDetalhe): RevisaoLinha[] => [
  {
    id: 'beneficiario',
    cells: {
      nome: displayValue(detalhe.nomeBeneficiario),
      cartao: displayValue(detalhe.codAntigoUsr),
      idade: '—',
    },
  },
];

export const mapProfissionalLinhas = (detalhe: TranscricaoDetalhe): RevisaoLinha[] => {
  const nome =
    detalhe.nomeSolicitanteInformado?.trim()
    || detalhe.nomeSolicitante?.trim()
    || undefined;

  return [
    {
      id: 'profissional',
      cells: {
        nome: displayValue(nome),
        registro: displayValue(detalhe.numeroConselhoInformado),
        uf: displayValue(detalhe.siglaUfConselhoInformado),
        tipo: displayValue(detalhe.codigoConselhoInformado),
        cooperado: formatCooperado(detalhe.indCooperado),
      },
    },
  ];
};

export const mapCidLinhas = (detalhe: TranscricaoDetalhe): RevisaoLinha[] => [
  {
    id: 'cid',
    cells: {
      cid: displayValue(resolveCodigoCid(detalhe)),
      descricao: displayValue(resolveDescrCid(detalhe)),
      emissao: formatDataCadastro(detalhe.dataCadastro),
    },
  },
];

/** Detalhes da Transcrição — Emissão do Pedido usa DT_COMPET_ARQ (dataCompetenciaArquivo). */
export const mapCidLinhasDetalhe = (detalhe: TranscricaoDetalhe): RevisaoLinha[] => [
  {
    id: 'cid',
    cells: {
      cid: displayValue(resolveCodigoCid(detalhe)),
      descricao: displayValue(resolveDescrCid(detalhe)),
      emissao: formatDataCadastro(detalhe.dataCompetenciaArquivo),
    },
  },
];

export const mapProcedimentosParaLinhas = (procedimentos: TranscricaoItemDetalhe[]): RevisaoLinha[] =>
  procedimentos.map((item) => ({
    id: item.idTranscricaoItem,
    cells: {
      tipoItem: formatTipoItemLabel(item.tipoProdMed),
      codigoTuss: displayValue(item.codigo),
      descricao: displayValue(item.descricao),
      quantidade: formatQuantidade(item.quantidade),
    },
  }));

export const parseQuantidade = (quantidade: string): number => {
  const parsed = Number.parseInt(quantidade, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const colunasParaRowsEmpilhadas = (
  colunas: RevisaoColuna[],
  linha: RevisaoLinha,
): DadosPedidoCardRow[] =>
  colunas.map((coluna) => ({
    fields: [{ label: coluna.label, value: linha.cells[coluna.key] ?? '—' }],
  }));

const isProfissionalColunas = (colunas: RevisaoColuna[]): boolean =>
  colunas.some((coluna) => coluna.key === 'registro')
  && colunas.some((coluna) => coluna.key === 'cooperado');

export const mapLinhaToMobileRows = (
  colunas: RevisaoColuna[],
  linha: RevisaoLinha,
): DadosPedidoCardRow[] => {
  const dataColunas = colunas.filter((coluna) => !coluna.isEdit);

  if (isProfissionalColunas(dataColunas)) {
    return mapProfissionalRowsMobile([linha]);
  }

  return colunasParaRowsEmpilhadas(dataColunas, linha);
};

export const mapProfissionalRowsMobile = (profissionalLinhas: RevisaoLinha[]): DadosPedidoCardRow[] => {
  const cells = profissionalLinhas[0]?.cells ?? {};

  return [
    { fields: [{ label: 'Profissional', value: cells.nome ?? '—' }] },
    {
      fields: [
        { label: 'Nº Registro', value: cells.registro ?? '—' },
        { label: 'UF', value: cells.uf ?? '—' },
      ],
    },
    {
      fields: [
        { label: 'Tipo', value: cells.tipo ?? '—' },
        { label: 'Cooperado', value: cells.cooperado ?? '—' },
      ],
    },
  ];
};
